/**
 * <tf-search> — Search bar premium icon-only collapsible.
 *
 * Wrap del legacy `assets/tf-search.js` come custom element TS.
 * Offre la stessa UX (40px → 240px expand on click) ma con type safety.
 *
 * Uso HTML:
 *   <tf-search id="cerca-clienti" placeholder="Cerca cliente..." debounce="200"></tf-search>
 *
 * Attributes:
 *   - placeholder: string (default: "Cerca…")
 *   - value: string (initial value)
 *   - debounce: number (ms, default: 200)
 *   - width: string (CSS expanded width, default: "240px")
 *
 * Events:
 *   - tf-search-input: dispatched dopo debounce con detail: { value: string }
 *   - tf-search-clear: dispatched quando user pulisce input
 *   - tf-search-expand: dispatched quando si espande
 *   - tf-search-collapse: dispatched quando si chiude
 *
 * Methods (via element ref):
 *   - element.expand()
 *   - element.collapse()
 *   - element.focus()
 *   - element.value getter/setter
 *
 * @since v9.16 (Sprint 7)
 */

export class TfSearchElement extends HTMLElement {
  private _input?: HTMLInputElement;
  private _wrapper?: HTMLDivElement;
  private _isExpanded = false;
  private _debounceTimer: number | null = null;

  static get observedAttributes(): string[] {
    return ['placeholder', 'value', 'debounce', 'width'];
  }

  connectedCallback(): void {
    this._render();
  }

  disconnectedCallback(): void {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  attributeChangedCallback(name: string, _oldVal: string, newVal: string): void {
    if (!this._input) return;
    if (name === 'placeholder') this._input.placeholder = newVal || 'Cerca…';
    if (name === 'value') this._input.value = newVal || '';
  }

  // ============================================================
  // Public API
  // ============================================================

  get value(): string {
    return this._input?.value || '';
  }

  set value(v: string) {
    if (this._input) this._input.value = v;
  }

  expand(): void {
    if (!this._wrapper || this._isExpanded) return;
    this._isExpanded = true;
    this._wrapper.classList.add('tf-search-expanded');
    setTimeout(() => this._input?.focus(), 50);
    this.dispatchEvent(new CustomEvent('tf-search-expand', { bubbles: true }));
  }

  collapse(): void {
    if (!this._wrapper || !this._isExpanded) return;
    if (this._input?.value) return; // Don't collapse if has value
    this._isExpanded = false;
    this._wrapper.classList.remove('tf-search-expanded');
    this.dispatchEvent(new CustomEvent('tf-search-collapse', { bubbles: true }));
  }

  override focus(): void {
    this._input?.focus();
  }

  // ============================================================
  // Internal
  // ============================================================

  private _render(): void {
    const placeholder = this.getAttribute('placeholder') || 'Cerca…';
    const initialValue = this.getAttribute('value') || '';
    const width = this.getAttribute('width') || '240px';

    this.innerHTML = `
      <div class="tf-search-wrapper inline-flex items-center" style="--tf-search-width:${width}">
        <button type="button" class="tf-search-icon-btn flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.65] hover:bg-white/[0.85] backdrop-blur-md transition-all border border-stroke-1 shadow-sm" aria-label="Apri ricerca">
          <svg class="size-4 text-secondary/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/></svg>
        </button>
        <input
          type="search"
          class="tf-search-input"
          placeholder="${this._escapeHtml(placeholder)}"
          value="${this._escapeHtml(initialValue)}"
          inputmode="search"
          enterkeyhint="search"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
        />
        <button type="button" class="tf-search-clear hidden" aria-label="Pulisci">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `;

    this._wrapper = this.querySelector<HTMLDivElement>('.tf-search-wrapper')!;
    this._input = this.querySelector<HTMLInputElement>('.tf-search-input')!;
    const iconBtn = this.querySelector<HTMLButtonElement>('.tf-search-icon-btn')!;
    const clearBtn = this.querySelector<HTMLButtonElement>('.tf-search-clear')!;

    // Click icon → expand (focus input sync per iOS)
    iconBtn.addEventListener('click', () => {
      if (!this._isExpanded) {
        this.expand();
      } else {
        this._input?.focus();
      }
    });

    // Input event with debounce
    this._input.addEventListener('input', () => {
      const value = this._input!.value;

      // Show/hide clear button
      if (value) {
        clearBtn.classList.remove('hidden');
      } else {
        clearBtn.classList.add('hidden');
      }

      // Debounced event
      const debounce = parseInt(this.getAttribute('debounce') || '200', 10);
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      this._debounceTimer = window.setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent('tf-search-input', {
            bubbles: true,
            detail: { value },
          })
        );
      }, debounce);
    });

    // Clear button
    clearBtn.addEventListener('click', () => {
      this._input!.value = '';
      clearBtn.classList.add('hidden');
      this._input!.focus();
      this.dispatchEvent(new CustomEvent('tf-search-clear', { bubbles: true }));
      this.dispatchEvent(
        new CustomEvent('tf-search-input', {
          bubbles: true,
          detail: { value: '' },
        })
      );
    });

    // Blur → collapse if empty
    this._input.addEventListener('blur', () => {
      setTimeout(() => {
        if (!this._input?.value) this.collapse();
      }, 200);
    });

    // ESC → clear + collapse
    this._input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this._input!.value = '';
        clearBtn.classList.add('hidden');
        this.collapse();
        this.dispatchEvent(
          new CustomEvent('tf-search-input', {
            bubbles: true,
            detail: { value: '' },
          })
        );
      }
    });

    // Show clear if initial value
    if (initialValue) {
      clearBtn.classList.remove('hidden');
      this.expand();
    }

    // Inject styles solo una volta
    this._injectStyles();
  }

  private _injectStyles(): void {
    if (document.getElementById('tf-search-styles')) return;
    const style = document.createElement('style');
    style.id = 'tf-search-styles';
    style.textContent = `
      .tf-search-wrapper {
        position: relative;
        height: 40px;
        transition: width 280ms cubic-bezier(0.4, 0, 0.2, 1);
        width: 40px;
      }
      .tf-search-wrapper.tf-search-expanded {
        width: var(--tf-search-width, 240px);
      }
      .tf-search-icon-btn {
        flex-shrink: 0;
        z-index: 2;
        position: relative;
        cursor: pointer;
        will-change: transform;
        -webkit-tap-highlight-color: transparent;
      }
      .tf-search-input {
        position: absolute;
        right: 0;
        top: 0;
        width: 100%;
        height: 100%;
        padding: 0 36px 0 44px;
        border-radius: 9999px;
        border: 1px solid #e2e8f0;
        background: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(12px) saturate(180%);
        -webkit-backdrop-filter: blur(12px) saturate(180%);
        font-size: 14px;
        outline: none;
        opacity: 0;
        pointer-events: none;
        transition: opacity 200ms ease, border-color 150ms ease;
        transform: translateZ(0);
        -webkit-appearance: none;
        appearance: none;
      }
      .tf-search-input::-webkit-search-cancel-button,
      .tf-search-input::-webkit-search-decoration {
        -webkit-appearance: none;
        display: none;
      }
      .tf-search-wrapper.tf-search-expanded .tf-search-input {
        opacity: 1;
        pointer-events: auto;
      }
      .tf-search-input:focus {
        border-color: #f59e0b;
        background: rgba(255, 255, 255, 0.95);
      }
      .tf-search-clear {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: transparent;
        color: #94a3b8;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 150ms ease;
      }
      .tf-search-clear:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #475569;
      }
      .tf-search-clear.hidden {
        display: none;
      }
    `;
    document.head.appendChild(style);
  }

  private _escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
    );
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tf-search')) {
  customElements.define('tf-search', TfSearchElement);
}
